import {
  BASE_PRICES,
  DEADLINE_SHORTENING_RATES,
  POSTAL_FEE,
  REMOTE_AREA_FEE,
  SIMPLE_WITH_PHOTO_PRICES,
  STANDARD_DELIVERIES,
  VEHICLE_REQUIRED_FEE,
} from "../constants/priceMaster";
import {
  type AreaCode,
  type EstimateInput,
  type EstimateResult,
  type RequestType,
  type SpecialOptions,
} from "../types";

const getSiteAreaRate = (options: SpecialOptions): number => {
  const value = options.siteAreaSqm ?? 0;
  if (value < 500) return 0;
  if (value >= 5000) return 0.5;
  if (value >= 3000) return 0.3;
  if (value >= 1000) return 0.2;
  return 0.1;
};

const getSubdivisionRate = (options: SpecialOptions): number => {
  const value = options.subdivisionUnits ?? 0;
  if (value < 3) return 0;
  if (value >= 7) return 0.3;
  if (value >= 5) return 0.2;
  return 0.1;
};

const getCondominiumRate = (options: SpecialOptions): number => {
  const value = options.condominiumUnits ?? 0;
  if (value <= 0) return 0;
  if (value >= 201) return 0.8;
  if (value >= 101) return 0.5;
  if (value >= 51) return 0.3;
  if (value >= 21) return 0.2;
  return 0.1;
};

const getRegisteredLotsRate = (options: SpecialOptions): number => {
  const value = options.registeredLots ?? 0;
  if (value < 5) return 0;
  if (value >= 15) return 0.3;
  if (value >= 10) return 0.2;
  return 0.1;
};

/** 現地へ行く種別（簡易調査・詳細調査）。特別加算・僻地加算・車両必須加算はこの種別にだけかける */
export const isResearchType = (requestType: RequestType): boolean =>
  requestType === "simple" || requestType === "detailed";

export const getSpecialAdditionRate = (
  requestTypes: RequestType[],
  options: SpecialOptions
): number => {
  if (!requestTypes.some(isResearchType)) return 0;

  return Math.max(
    getSiteAreaRate(options),
    getSubdivisionRate(options),
    getCondominiumRate(options),
    getRegisteredLotsRate(options)
  );
};

const CONSULTATION = "要相談";

const businessDays = (delivery: string): number =>
  Number(/^(\d+)営業日$/.exec(delivery)?.[1] ?? Number.NaN);

/** 選んだ種別の中で最も長い納期を返す。どれかが要相談なら要相談 */
export const getStandardDelivery = (
  requestTypes: RequestType[],
  area: AreaCode
): string => {
  const deliveries = requestTypes.map(
    (requestType) => STANDARD_DELIVERIES[requestType][area]
  );
  if (deliveries.length === 0 || deliveries.includes(CONSULTATION)) {
    return CONSULTATION;
  }
  return deliveries.reduce((longest, delivery) =>
    businessDays(delivery) > businessDays(longest) ? delivery : longest
  );
};

const getBasePrice = (
  requestType: RequestType,
  input: EstimateInput
): number | null =>
  requestType === "simple" && input.onsitePhoto
    ? SIMPLE_WITH_PHOTO_PRICES[input.area]
    : BASE_PRICES[requestType][input.area];

const sumOrNull = (prices: Array<number | null>): number | null =>
  prices.some((price) => price === null)
    ? null
    : prices.reduce<number>((sum, price) => sum + (price ?? 0), 0);

export const calculateEstimate = (input: EstimateInput): EstimateResult => {
  const basePriceItems = input.requestTypes.map((requestType) => ({
    requestType,
    basePrice: getBasePrice(requestType, input),
  }));
  const basePrice = sumOrNull(basePriceItems.map((item) => item.basePrice));
  const simpleStandardPrice = BASE_PRICES.simple[input.area];
  const simpleWithPhotoPrice = SIMPLE_WITH_PHOTO_PRICES[input.area];
  const onsitePhotoFee =
    input.requestTypes.includes("simple") &&
    input.onsitePhoto &&
    simpleWithPhotoPrice !== null &&
    simpleStandardPrice !== null
      ? simpleWithPhotoPrice - simpleStandardPrice
      : 0;
  const shorteningRate = DEADLINE_SHORTENING_RATES[input.deadlineShortening];
  const specialAdditionRate = getSpecialAdditionRate(
    input.requestTypes,
    input.specialOptions
  );
  // 特別加算は簡易調査・詳細調査の基本価格にだけ掛け、納期短縮は基本価格の合計に掛ける
  const researchBasePrice = sumOrNull(
    basePriceItems
      .filter((item) => isResearchType(item.requestType))
      .map((item) => item.basePrice)
  );
  const shorteningAmount =
    basePrice === null ? 0 : Math.round(basePrice * shorteningRate);
  // 他の種別が個別見積でも、金額が決まっている調査分の加算は出す
  const specialAdditionAmount =
    researchBasePrice === null
      ? 0
      : Math.round(researchBasePrice * specialAdditionRate);
  // 現地へ行く種別（簡易調査・詳細調査）だけに、最寄駅からの距離で1回加算する
  // 判定できなかったとき（judged が偽）は、加算の印が付いていても加算しない（1ページ目の表示とそろえる）
  const goesOnsite =
    input.requestTypes.some(isResearchType) && input.access.judged;
  const remoteAreaFee =
    goesOnsite && input.access.remoteArea ? REMOTE_AREA_FEE : 0;
  const vehicleFee =
    goesOnsite && input.access.vehicleRequired ? VEHICLE_REQUIRED_FEE : 0;
  const postalFee = input.needsPostalDelivery ? POSTAL_FEE : 0;
  const total =
    basePrice === null || input.deliveryQuoteRequired
      ? null
      : basePrice +
        shorteningAmount +
        specialAdditionAmount +
        remoteAreaFee +
        vehicleFee +
        postalFee;

  return {
    area: input.area,
    basePrice,
    basePriceItems,
    onsitePhotoFee,
    shorteningRate,
    shorteningAmount,
    specialAdditionRate,
    specialAdditionAmount,
    remoteAreaFee,
    vehicleFee,
    access: input.access,
    deliveryQuoteRequired: input.deliveryQuoteRequired,
    postalFee,
    total,
    standardDelivery: getStandardDelivery(input.requestTypes, input.area),
  };
};
